import React, { useState, useEffect } from 'react';
import { Modal, InputNumber, Button, Space, App, Tag, notification, Form, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { InventoryLocationItem, OrderAddV2, OrderAddItemV2, SpecialHandlingConfirm, WLocation, OrderV2, OrderItemV2 } from '@/types/inventoryV2';
import { ChangeEnum, OperateEnum } from '@/types/inventory';
import { getLocationCode } from '@/pages/Warehouse/utils';
import { orderAdd, orderConfirm, orderDel } from '@/apis/inventoryv2';
import SelectBarcode from '../SelectBarcode';
import SText from '@/styles/SText';
import useFrontDelTip from '@/hooks/useFrontDelTip';
import OrderConfirmInspection from '@/components/OrderConfirmInspection';
import CommonTable from "@/components/CommonTable";

interface LocationOutboundProps {
  lotItems: InventoryLocationItem[];
  open: boolean;
  onCancel: () => void;
  locationNo?: string;
  onSuccess: (data: OrderAddV2) => void;
}

interface TableRecord extends InventoryLocationItem {
  key: string;
  lotStr: string;
  lot: string;
  thdLot: string;
  productCode: string;
  productName: string;
  locationCode: string;
  maxOut: number;
  outNum: number;
  outBarcodes: string[];
}

const LocationOutbound: React.FC<LocationOutboundProps> = ({
  lotItems,
  open,
  onCancel,
  onSuccess,
  locationNo,
}) => {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [dataSource, setDataSource] = useState<TableRecord[]>([]);
  const [remark, setRemark] = useState<string>('');
  const [openInspection, setOpenInspection] = useState(false);
  const [inspectionPending, setInspectionPending] = useState<OrderItemV2[]>([]);
  const [pendingOrder, setPendingOrder] = useState<OrderAddV2 | null>(null);

  const [selectBarcodeState, setSelectBarcodeState] = useState<{
    visible: boolean;
    recordKey: string;
    barcodes: string[] | null;
    lotStr: string;
    locationId: string;
    selectedValues: string[];
  }>({
    visible: false,
    recordKey: '',
    barcodes: null,
    lotStr: '',
    locationId: '',
    selectedValues: [],
  });

  const doOutbound = async () => {
    const items = dataSource
      .filter((item) => item.outNum > 0)
      .map((item) => ({
        sourceId: '',
        productId: item.lotItem?.product?.id || '',
        count: item.outNum,
        lot: item.lotStr,
        thdlot: item.thdLot,
        desc: '',
        locations: [
          {
            locationId: item.locationId,
            count: item.outNum,
            barcodes: item.outBarcodes,
          },
        ],
      }));
    const data: OrderAddV2 = {
      type: ChangeEnum.OUT,
      opType: OperateEnum.TEMPORARY,
      desc: remark,
      preTime: null,
      arrivalAt: null,
      items,
    };

    setPendingOrder(data);
    setInspectionPending(dataSource.filter(item => item.outNum > 0).map(item => ({
      id: item.lotItemId,
      productId: item.lotItem?.product?.id || '',
      count: item.outNum,
      lot: item.lotStr,
      locations: [{
        id: item.lotItemId,
        locationId: item.locationId,
        orderItemId: item.lotItemId,
        count: item.outNum,
        barcodes: [],
        location: item.location,
      }],
    }) as unknown as OrderItemV2));
    setOpenInspection(true);
  };

  const doConfirm = async (specialHandling: SpecialHandlingConfirm[]) => {
    setConfirming(true);
    let order: OrderV2 | null = null;
    try {
      order = await orderAdd(pendingOrder!);
    } catch {
      notification.error({ placement: 'top', message: t('warehouse.outboundError') });
      setConfirming(false);
      return;
    }
    try {
      await orderConfirm(order.id, 0, specialHandling);
      // try {
      //   await orderConfirm(order.id, 0, specialHandling);
      // } catch (error: any) {
      //   if (error?.code === 70845) await orderConfirm(order.id, 0);
      //   else throw error
      // }
      notification.success({ placement: 'top', message: t('warehouse.outboundSuccess') });
      onSuccess(pendingOrder!);
    } catch (error: any) {
      if (error?.code === 73011) {
        let ids: string[] = (error?.extra ?? []);
        let codes = (order?.items ?? []).filter(item => ids.includes(item.productId)).map(p => p.product.code).join(', ')
        notification.error({
          message: t('warehouse.barcodeErr'),
          description: codes
        });
        // notification.error({
        //   placement: 'top',
        //   message: t('warehouse.outboundError'),
        //   description: t('warehouse.inventoryMismatch')
        // });
      } else if (error === 72811) {
        notification.error({
          placement: 'top',
          message: t('warehouse.outboundError'),
          description: t('product.dataError')
        });
      } else if (error?.code === 73007 || error?.code === 73008) {
        await orderDel(order.id);
        if (Array.isArray(error?.extra)) {
          let errCodes = dataSource
            .filter(item => error.extra.includes(item.lotItem?.product?.id) || error.extra.includes(item.productCode))
            .map(item => item.productCode ?? '')
            .join(', ')
          notification.error({
            placement: 'top',
            message: t('apiError.error31'),
            description: errCodes,
          });
        } else notification.error({
          placement: 'top',
          message: t('warehouse.outboundError'),
          description: t('apiError.error31'),
        });
      } else if (error === 72904 || error === 72906) {
        await orderDel(order.id);
        notification.error({
          placement: 'top',
          message: t('warehouse.outboundError'),
          description: t('warehouse.barcodeErr'),
        });
      } else if (error === 73010) {
        // 其他错误
        notification.error({
          placement: 'top',
          message: t('warehouse.outFail'),
          description: t('warehouse.locFailed'),
        });
      } else {
        notification.error({
          placement: 'top',
          message: t('warehouse.outboundError'),
          description: t('requirement.validError'),
        });
      }
    } finally {
      setConfirming(false);
    }
  };

  const { onConfirm: confirmOutbound } = useFrontDelTip<void>({
    cb: () => doOutbound(),
    message: t('warehouse.confirmOutboundTip'),
    desc: t('warehouse.confirmOutboundDesc'),
  });

  useEffect(() => {
    if (open) {
      setDataSource(
        lotItems.map((item) => {
          const lot = item.lotItem?.lot;
          const count = item.count || 0;
          const preOut = item.preOut || 0;
          return {
            ...item,
            key: `${item.lotItemId}-${item.locationId}`,
            lotStr: lot?.lot || '',
            lot: lot?.lot || '',
            thdLot: lot?.thdLot || '',
            productCode: item.lotItem?.product?.code || '',
            productName: item.lotItem?.product?.name || '',
            locationCode: getLocationCode(item.location),
            maxOut: count - preOut,
            outNum: 0,
            outBarcodes: [],
          };
        })
      );
    }
  }, [open, lotItems]);

  const handleOutNumChange = (key: string, value: number | null) => {
    setDataSource((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, outNum: value || 0 } : item
      )
    );
  };

  const handleSelectBarcode = (record: TableRecord) => {
    setSelectBarcodeState({
      visible: true,
      recordKey: record.key,
      barcodes: null,
      lotStr: record.lotStr,
      locationId: record.locationId,
      selectedValues: record.outBarcodes,
    });
  };

  const handleBarcodeConfirm = (values: string[]) => {
    const { recordKey } = selectBarcodeState;
    setDataSource((prev) =>
      prev.map((item) =>
        item.key === recordKey
          ? { ...item, outBarcodes: values, outNum: values.length }
          : item
      )
    );
    setSelectBarcodeState({
      visible: false,
      recordKey: '',
      barcodes: null,
      lotStr: '',
      locationId: '',
      selectedValues: [],
    });
  };

  const handleBarcodeClose = () => {
    setSelectBarcodeState({
      visible: false,
      recordKey: '',
      barcodes: null,
      lotStr: '',
      locationId: '',
      selectedValues: [],
    });
  };

  const columns: ColumnsType<TableRecord> = [
    {
      title: t('warehouse.locationCode'),
      dataIndex: 'locationCode',
      key: 'locationCode',
      width: 120,
      fixed: 'left',
      render: (text) => <SText text={text || ''} />,
    },
    {
      title: t('warehouse.batchNo'),
      dataIndex: 'lot',
      key: 'lot',
      width: 120,
      render: (text) => <SText text={text || ''} />,
    },
    {
      title: t('order.quantity'),
      dataIndex: 'count',
      key: 'count',
      width: 80,
      render: (value) => value || 0,
    },
    {
      title: t('warehouse.preIn'),
      dataIndex: 'preIn',
      key: 'preIn',
      width: 120,
      render: (value) => value || 0,
    },
    {
      title: t('warehouse.preOut'),
      dataIndex: 'preOut',
      key: 'preOut',
      width: 120,
      render: (value) => value || 0,
    },
    {
      title: t('warehouse.supplierBatchNo'),
      dataIndex: 'thdLot',
      key: 'thdLot',
      width: 120,
      render: (text) => <SText text={text || ''} />,
    },
    {
      title: t('product.code'),
      dataIndex: 'productCode',
      key: 'productCode',
      width: 120,
      render: (text) => <SText text={text || ''} />,
    },
    {
      title: t('product.name'),
      dataIndex: 'productName',
      key: 'productName',
      width: 150,
      render: (text) => <SText text={text || ''} />,
    },
    {
      title: t('warehouse.outNum'),
      dataIndex: 'outNum',
      key: 'outNum',
      width: 120,
      fixed: "right",
      render: (value, record) => (
        <InputNumber changeOnWheel
          min={record?.outBarcodes?.length || 0}
          max={record.maxOut}
          value={value}
          onChange={(val) => handleOutNumChange(record.key, val)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('warehouse.outBarcodes'),
      dataIndex: 'outBarcodes',
      key: 'outBarcodes',
      width: 200,
      fixed: "right",
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Button
            type="link"
            size="small"
            onClick={() => handleSelectBarcode(record)}
          >
            {t('warehouse.selectBarcode')}（{record.outBarcodes?.length || 0}）
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={
          <Space>
            {t('warehouse.locationOutbound')}
            {locationNo && <Tag color="blue">{locationNo}</Tag>}
          </Space>
        }
        open={open}
        onCancel={onCancel}
        onOk={() => {
          if (dataSource.find(item => item.outNum > 0)) {
            confirmOutbound()
          } else {
            notification.error({ placement: 'top', message: t('warehouse.plOutQuantity') });
          }
        }}
        width={1200}
        okText={t('common.confirm')}
        cancelText={t('common.cancel')}
        confirmLoading={confirming}
      >
        <CommonTable
          title={() => (
            <Form.Item label={t('purchase.remark')} style={{ flex: 1 }}>
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} value={remark} onChange={e => setRemark(e.target.value)} maxLength={500} showCount />
            </Form.Item>
          )}
          columns={columns}
          dataSource={dataSource}
          rowKey="key"
          virtual
          pagination={false}
          bordered={false}
          size="small"
          scroll={{ x: 1270, y: 400 }}
        />
      </Modal>
      <SelectBarcode
        barcodes={selectBarcodeState.barcodes}
        lotStr={selectBarcodeState.lotStr}
        locationId={selectBarcodeState.locationId}
        open={selectBarcodeState.visible}
        values={selectBarcodeState.selectedValues}
        onConfirm={handleBarcodeConfirm}
        onClose={handleBarcodeClose}
      />
      {/* 巡检弹窗：onOk 后执行出库确认逻辑 */}
      <OrderConfirmInspection
        items={inspectionPending}
        open={openInspection}
        onOk={(data) => {
          setOpenInspection(false);
          doConfirm(data);
        }}
        onClose={() => setOpenInspection(false)}
      />
    </>
  );
};

export default LocationOutbound;
